import React, { useState } from 'react';
import { supabase } from './supabaseClient';

const DISPOSABLE_DOMAINS = new Set([
  'mailinator.com','guerrillamail.com','guerrillamail.net','guerrillamail.org',
  'guerrillamail.biz','guerrillamail.de','guerrillamail.info','grr.la',
  'sharklasers.com','guerrillamailblock.com','spam4.me','trashmail.com',
  'trashmail.me','trashmail.net','trashmail.at','trashmail.io','trashmail.xyz',
  'yopmail.com','yopmail.fr','cool.fr.nf','jetable.fr.nf','nospam.ze.tc',
  'nomail.xl.cx','mega.zik.dj','speed.1s.fr','courriel.fr.nf','moncourrier.fr.nf',
  'monemail.fr.nf','monmail.fr.nf','dispostable.com','tempr.email','discard.email',
  'maildrop.cc','spamgourmet.com','spamgourmet.net','spamgourmet.org',
  'throwam.com','throwaway.email','mailnull.com','spamspot.com',
  'spamthisplease.com','fakemail.net','fakeinbox.com','mailnew.com',
  'tempmail.com','tempmail.net','tempmail.org','temp-mail.org','temp-mail.de',
  'temp-mail.io','tempinbox.com','temporaryemail.net','temporaryinbox.com',
  '10minutemail.com','10minutemail.net','10minutemail.org','minutemail.com',
  'moakt.cc','moakt.co','moakt.com','moakt.ws','mohmal.com',
]);

function isDisposableEmail(email) {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return DISPOSABLE_DOMAINS.has(domain);
}

function AuthPage({ onBack }) {
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [isSignUp, setIsSignUp]         = useState(true); // Default to Sign Up
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [message, setMessage]           = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (isSignUp && isDisposableEmail(email)) {
      setError('Please use a real email address. Disposable addresses are not allowed.');
      setLoading(false);
      return;
    }

    if (isSignUp) {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) setError(error.message);
      else setMessage('Check your email to confirm your account!');
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#060d1e',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', system-ui, sans-serif",
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Background glow */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(16,185,129,0.08) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        backgroundColor: '#0d1a2d',
        borderRadius: '20px',
        padding: '48px 44px',
        width: '100%',
        maxWidth: '460px',
        border: '1px solid #1e3a5f',
        boxSizing: 'border-box',
        position: 'relative',
        boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
      }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width: '56px', height: '56px',
            background: 'linear-gradient(135deg, #10b981, #3b82f6)',
            borderRadius: '14px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '16px',
          }}>
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <polyline points="3,17 7,17 9,17 11,11 13,23 15,16 17,16 19,13 21,18 23,16 26,16 29,14"
                fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="29" cy="14" r="2" fill="white"/>
            </svg>
          </div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: '#f0f6ff', letterSpacing: '-0.5px', marginBottom: '6px' }}>
            Alpha<span style={{ color: '#10b981' }}>Bio</span><span style={{ fontWeight: '300', color: '#8ba5c8' }}>AI</span>
          </div>
          <div style={{ fontSize: '14px', color: '#8ba5c8', fontWeight: '500' }}>
            {isSignUp ? 'Start your 7-day free trial' : 'Welcome back'}
          </div>
        </div>

        {/* Tab switcher */}
        <div style={{
          display: 'flex',
          backgroundColor: '#060d1e',
          borderRadius: '10px',
          padding: '4px',
          marginBottom: '28px',
          border: '1px solid #1e3a5f',
        }}>
          {['Sign Up', 'Sign In'].map((tab, i) => {
            const active = (i === 0) === isSignUp;
            return (
              <button
                key={tab}
                onClick={() => { setIsSignUp(i === 0); setError(''); setMessage(''); }}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '7px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: active ? '700' : '500',
                  fontFamily: 'inherit',
                  background: active ? '#10b981' : 'transparent',
                  color: active ? 'white' : '#8ba5c8',
                  transition: 'all 0.2s',
                }}
              >{tab}</button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#8ba5c8', marginBottom: '7px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Email address
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '13px 16px',
                borderRadius: '10px',
                border: '1px solid #1e3a5f',
                backgroundColor: '#060d1e',
                color: '#f0f6ff',
                fontSize: '15px',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#10b981'}
              onBlur={e => e.target.style.borderColor = '#1e3a5f'}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '22px', position: 'relative' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#8ba5c8', marginBottom: '7px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={isSignUp ? 'Create a password (min 8 chars)' : 'Your password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '13px 56px 13px 16px',
                borderRadius: '10px',
                border: '1px solid #1e3a5f',
                backgroundColor: '#060d1e',
                color: '#f0f6ff',
                fontSize: '15px',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#10b981'}
              onBlur={e => e.target.style.borderColor = '#1e3a5f'}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '14px',
                bottom: '13px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#4a6a8a',
                fontSize: '11px',
                fontWeight: '700',
                fontFamily: 'inherit',
                padding: '0',
                letterSpacing: '0.5px',
              }}
            >
              {showPassword ? 'HIDE' : 'SHOW'}
            </button>
          </div>

          {/* Error / Success */}
          {error && (
            <div style={{
              color: '#ef4444', fontSize: '13px', marginBottom: '16px',
              lineHeight: '1.5', background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px',
              padding: '10px 14px',
            }}>
              {error}
            </div>
          )}
          {message && (
            <div style={{
              color: '#10b981', fontSize: '13px', marginBottom: '16px',
              lineHeight: '1.5', background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.2)', borderRadius: '8px',
              padding: '10px 14px',
            }}>
              {message}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? '#0a8a65' : 'linear-gradient(135deg, #10b981, #0d9e72)',
              color: 'white',
              border: 'none',
              padding: '14px',
              borderRadius: '10px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily: 'inherit',
              opacity: loading ? 0.8 : 1,
              transition: 'all 0.2s',
              letterSpacing: '-0.2px',
            }}
            onMouseEnter={e => { if (!loading) e.target.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.target.style.opacity = '1'; }}
          >
            {loading ? 'Loading...' : isSignUp ? 'Start Free Trial →' : 'Sign In →'}
          </button>
        </form>

        {/* Trial note */}
        {isSignUp && (
          <div style={{
            textAlign: 'center', marginTop: '16px',
            display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap',
          }}>
            {['No charge for 7 days', 'Cancel anytime'].map((t, i) => (
              <span key={i} style={{ fontSize: '12px', color: '#4a6a8a', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ color: '#10b981', fontWeight: '700' }}>✓</span>{t}
              </span>
            ))}
          </div>
        )}

        {/* Back to home */}
        {onBack && (
          <button
            onClick={onBack}
            style={{
              marginTop: '20px', width: '100%',
              backgroundColor: 'transparent',
              border: '1px solid #1e3a5f',
              color: '#4a6a8a', padding: '11px',
              borderRadius: '10px', cursor: 'pointer',
              fontSize: '13px', fontFamily: 'inherit',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.target.style.borderColor = '#2d5a8e'; e.target.style.color = '#8ba5c8'; }}
            onMouseLeave={e => { e.target.style.borderColor = '#1e3a5f'; e.target.style.color = '#4a6a8a'; }}
          >
            ← Back to Home
          </button>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        input::placeholder { color: #2d5a8e; }
      `}</style>
    </div>
  );
}

export default AuthPage;